import React from 'react'
import { useIntl } from 'react-intl';
import { Popup, Table } from 'semantic-ui-react';
import { getColorIndicator } from '../../../libs/common_utils';
import LegendBar from './LegendBar';
import LegendPill from './LegendPill';

const FlightPacingPopup = ({ data, nullishCurrencyFormat }) => {
  const intl = useIntl();
  const [open, setOpen] = React.useState(false);
  const closeTimer = React.useRef(null);
  const pct = data.campaign_flight_pacing_at_percent ?? null;
  const budget = nullishCurrencyFormat(data.budget);
  const actual = nullishCurrencyFormat(data.campaign_flight_pacing_actual_spend);
  const ideal = nullishCurrencyFormat(data.campaign_flight_pacing_ideal_spend);
  const remaining = nullishCurrencyFormat(data.campaign_flight_pacing_remaining);
  let actualSpendPercent = 0;
  if (data.campaign_flight_pacing_actual_spend && data.campaign_flight_pacing_ideal_spend) {
    actualSpendPercent = (data.campaign_flight_pacing_actual_spend / data.campaign_flight_pacing_ideal_spend) * 100;
  }

  const clearCloseTimer = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  const scheduleClose = () => {
    clearCloseTimer();
    closeTimer.current = setTimeout(() => setOpen(false), 160); // grace window
  };

  const trigger = (
    <span
      onMouseEnter={() => { clearCloseTimer(); setOpen(true); }}
      onMouseLeave={scheduleClose}
      style={{ cursor: "pointer" }}
      tabIndex={1}
    >
      {nullishCurrencyFormat(data.campaign_flight_pacing_actual_spend)} /
      {nullishCurrencyFormat(data.budget)}
    </span>
  );

  const LegendTriangle = ({ color = '#2185d0' }) => (
    <span style={{
      display: 'inline-block', width: 0, height: 0,
      borderLeft: '6px solid transparent', borderRight: '6px solid transparent',
      borderBottom: `10px solid ${color}`, marginRight: 8, verticalAlign: 'middle'
    }} />
  );

  const content = (
          <div
            onMouseEnter={clearCloseTimer}
            onMouseLeave={scheduleClose}
            style={{
              minWidth: 520,
              padding: 16,
              display: 'grid',
              gridTemplateRows: 'auto 1px auto',
              rowGap: 12
            }}
          >
            {/* Header row */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                columnGap: 24,
                alignItems: 'center'
              }}
            >
              <div style={{ fontSize: 16, fontWeight: 600 }}>
                {intl.formatMessage({
                  id: 'LABEL_FLIGHT_PACING_AT',
                  defaultMessage: 'Flight Pacing at'
                })}{' '}
                {pct !== null ? `${pct}%` : '—'}
              </div>

              <div style={{ fontSize: 16, fontWeight: 600, textAlign: 'right' }}>
                {intl.formatMessage({
                  id: 'LABEL_BUDGET_OF',
                  defaultMessage: 'Budget of'
                })}{' '}
                {budget}
              </div>
            </div>

            {/* subtle divider */}
            <div style={{ height: 1, background: '#eee', borderRadius: 1 }} />

            {/* Legend blocks */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr 1fr',
                columnGap: 24
              }}
            >
              {/* ACTUAL */}
              <div>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    letterSpacing: 0.5,
                    color: '#555',
                    textTransform: 'uppercase',
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: 4,
                    gap: 4
                  }}
                >
                  <LegendBar color={getColorIndicator(actualSpendPercent)} progress={actualSpendPercent} />
                  {intl.formatMessage({
                    id: 'LABEL_SPEND_ACTUAL',
                    defaultMessage: 'Spend (Actual)'
                  })}
                </div>
                <div style={{ fontSize: 16 }}>{actual}</div>
              </div>

              {/* IDEAL */}
              <div>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    letterSpacing: 0.5,
                    color: '#555',
                    textTransform: 'uppercase',
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: 4
                  }}
                >
                  <LegendTriangle />
                  {intl.formatMessage({
                    id: 'LABEL_SPEND_IDEAL',
                    defaultMessage: 'Spend (Ideal)'
                  })}
                </div>
                <div style={{ fontSize: 16 }}>{ideal}</div>
              </div>

              {/* REMAINING */}
              <div>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    letterSpacing: 0.5,
                    color: '#555',
                    textTransform: 'uppercase',
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: 4
                  }}
                >
                  <LegendPill />
                  {intl.formatMessage({
                    id: 'LABEL_REMAINING',
                    defaultMessage: 'Remaining'
                  })}
                </div>
                <div style={{ fontSize: 16 }}>{remaining}</div>
              </div>
            </div>
          </div>
        );

  return (
    <Table.Cell key={`campaign-flight-pacing-${data.id}`}>
      <Popup
        hoverable
        position="top center"
        hideOnScroll={false}
        on="hover"
        wide
        open={open}
        content={content}
        trigger={trigger}
      />
    </Table.Cell>
  );
};

export default FlightPacingPopup